import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Box, Button, Chip, CircularProgress, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import JornadaService from "../../services/JornadaService";
import LogService from "../../services/LogService";
import ILog from "../../types/Log";
import IAlert from "../../types/Alert";
import { IIncidencia, IJornadaHoy, IPersonaHoy, ITramo } from "../../types/Jornada";
import { aHHmm, diaSemanaCorto, duracionFichada, horaFichaje, NOMBRE_FICHAJE } from "../../utils/Jornada";
import CorreccionDialog, { PeticionCorreccion } from "./CorreccionDialog";
import NoDisponibleDialog from "../config/NoDisponibleDialog";
import * as constantes from "../../common/Constantes";

// Pestaña «Hoy» (admin): quién está y qué falla. Arriba, las incidencias abiertas del mes, cada una con su forma de
// resolverla en línea; debajo, una tarjeta por persona que ficha con su estado y sus fichajes de hoy, que el admin puede
// corregir, anular o completar. Se carga al abrirla, sin refresco automático.
interface HoyJornadaProps {
    setAlert: (alert: IAlert) => void;
}

type Fallo = { message?: string; response?: { status?: number; data?: { message?: string } } };

const nombreDe = (p?: { firstName: string; lastName?: string }) => p ? `${p.firstName} ${p.lastName ?? ''}`.trim() : '';
const iniciales = (p: { firstName: string; lastName?: string }) => `${p.firstName?.[0] ?? ''}${p.lastName?.[0] ?? ''}`.toUpperCase();
// «8:00–14:00 · 16:00–19:00», sin el cero de delante como el resto de horas
const horario = (tramos: ITramo[]) => tramos.map(t => `${t.inicio.replace(/^0/, '')}–${t.fin.replace(/^0/, '')}`).join(' · ');

const HoyJornada = ({ setAlert }: HoyJornadaProps) => {
    const history = useNavigate();
    const [datos, setDatos] = useState<IJornadaHoy | null>(null);
    const [loading, setLoading] = useState(true);
    const [peticion, setPeticion] = useState<PeticionCorreccion | null>(null);
    // Crear un no disponible desde una incidencia, con la persona y el tramo ya puestos
    const [noDisponible, setNoDisponible] = useState<{ especialistaId: number; start: Date; end: Date } | null>(null);

    const cargar = useCallback(() => {
        setLoading(true);
        JornadaService.getHoy()
            .then((response: { data: IJornadaHoy }) => setDatos(response.data))
            .catch((e: unknown) => {
                const fallo = e as Fallo;
                if (fallo.response?.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                    return;
                }
                if (fallo.response?.status !== 403) {
                    const error: ILog = { method: 'HoyJornada.getHoy', level: 'error', message: fallo.message ?? '', meta: fallo.response };
                    LogService.logError(error);
                }
                setAlert({ open: true, severity: 'error', msg: fallo.response?.data?.message || 'No se ha podido cargar el día de hoy' });
            })
            .finally(() => setLoading(false));
    }, [history, setAlert]);

    useEffect(() => { cargar(); }, [cargar]);

    const guardado = (mensaje: string) => {
        setPeticion(null);
        setAlert({ open: true, severity: 'success', msg: mensaje });
        cargar();
    };

    if (loading && !datos) return <CircularProgress />;
    if (!datos) return null;
    if (!datos.activo) {
        return (
            <Box>
                <Typography variant="body1">El registro de jornada está desactivado. Puedes activarlo en Configuración › Usuarios.</Typography>
                <Button variant="text" sx={{ textTransform: 'none', p: 0, minWidth: 0 }} onClick={() => history(constantes.R_CONFIG)}>Ir a Configuración › Usuarios</Button>
            </Box>
        );
    }

    const persona = (especialistaId: number) => datos.personas.find(p => p.especialistaId === especialistaId);
    const dia = (fecha: string) => (fecha === datos.hoy ? 'Hoy' : diaSemanaCorto(fecha, true));

    // Texto y acciones de cada incidencia
    const incidencia = (i: IIncidencia) => {
        const p = persona(i.especialistaId);
        const nombre = nombreDe(p);
        const base = { especialistaId: i.especialistaId, nombre, fecha: i.fecha };
        switch (i.tipo) {
            case 'noHaFichado': {
                // Un día sin ningún fichaje llega como una sola incidencia con todos sus tramos
                const todoElDia = !!i.tramos;
                const tramos = (i.tramos ?? [{ inicio: i.inicio!, fin: i.fin! }]).map(t => `${horaFichaje(t.inicio)}–${horaFichaje(t.fin)}`).join(' · ');
                return {
                    texto: todoElDia
                        ? `${nombre} ${i.fecha === datos.hoy ? 'no ha fichado en todo el día' : 'no fichó en todo el día'}`
                        : `${nombre} ${i.fecha === datos.hoy ? 'no ha fichado la entrada' : 'no fichó la entrada'}`,
                    detalle: `${dia(i.fecha)} · ${todoElDia ? '' : 'tramo '}${tramos}`,
                    acciones: [
                        { texto: 'Añadir entrada', accion: () => setPeticion({ ...base, accion: 'anadir', tipo: 'entrada', hora: aHHmm(new Date(i.inicio!)) }) },
                        { texto: 'Crear no disponible', accion: () => setNoDisponible({ especialistaId: i.especialistaId, start: new Date(i.inicio!), end: new Date(i.fin!) }) },
                    ],
                };
            }
            case 'incompleta':
                return {
                    texto: `${nombre} no fichó la salida`,
                    detalle: `${dia(i.fecha)} · último fichaje: ${NOMBRE_FICHAJE[i.evento!.tipo].toLowerCase()} a las ${horaFichaje(i.evento!.hora)}`,
                    acciones: [{ texto: 'Añadir salida', accion: () => setPeticion({ ...base, accion: 'anadir', tipo: 'salida' }) }],
                };
            default: // horaDeclarada
                return {
                    texto: `${nombre} declaró la ${i.evento!.tipo} a las ${horaFichaje(i.evento!.hora)}`,
                    detalle: `${dia(i.fecha)} · a más de 2 h de su horario`,
                    acciones: [
                        { texto: 'Confirmar hora', accion: () => setPeticion({ ...base, accion: 'confirmar', evento: i.evento }) },
                        { texto: 'Corregir', accion: () => setPeticion({ ...base, accion: 'corregir', evento: i.evento, hora: aHHmm(new Date(i.evento!.hora)) }) },
                    ],
                };
        }
    };

    const estadoChip = (p: IPersonaHoy) => {
        const { estado, estadoDesde, noDisponibleAhora } = p.hoy;
        const desde = estadoDesde ? ` desde las ${horaFichaje(estadoDesde)}` : '';
        if (estado === 'enJornada') return <Chip size="small" color="success" label={`En jornada${desde}`} />;
        if (estado === 'enPausa') return <Chip size="small" color="warning" label={`En pausa${desde}`} />;
        if (noDisponibleAhora) return <Chip size="small" variant="outlined" label="No disponible" />;
        if (estado === 'cerrada') {
            // Jornada cerrada: desde la última salida
            const salida = p.hoy.eventos[p.hoy.eventos.length - 1];
            return <Chip size="small" variant="outlined" label={salida ? `Fuera desde las ${horaFichaje(salida.hora)}` : 'Fuera'} />;
        }
        return <Chip size="small" variant="outlined" label="Sin fichar" />;
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">{diaSemanaCorto(datos.hoy, true)} · actualizado a las {horaFichaje(datos.ahora)}</Typography>
                <Button size="small" startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />} disabled={loading} onClick={cargar}>Actualizar</Button>
            </Box>

            {/* INCIDENCIAS, solo si hay */}
            {datos.incidencias.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Incidencias ({datos.incidencias.length})</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Las de este mes que siguen abiertas. Desaparecen al resolverlas.</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {datos.incidencias.map((i, indice) => {
                            const { texto, detalle, acciones } = incidencia(i);
                            return (
                                <Paper key={`${i.tipo}-${i.especialistaId}-${i.fecha}-${indice}`} variant="outlined" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderLeft: 4, borderLeftColor: 'warning.main' }}>
                                    <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{texto}</Typography>
                                        <Typography variant="body2" color="text.secondary">{detalle}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {acciones.map((a, k) => <Button key={a.texto} size="small" variant={k === 0 ? 'outlined' : 'text'} onClick={a.accion}>{a.texto}</Button>)}
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* EQUIPO */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>Equipo</Typography>
            {datos.personas.length === 0 && (
                <Typography variant="body2" color="text.secondary">Nadie ficha todavía: actívalo en la pestaña Configuración.</Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {datos.personas.map(p => {
                    const nombre = nombreDe(p);
                    const { hoy } = p;
                    return (
                        // Avatar y datos siempre en la misma fila; en móvil, «Añadir fichaje» baja a otra, alineado con el nombre
                        <Paper variant="outlined" key={p.especialistaId} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', columnGap: 2, rowGap: 0.5, px: 2, py: 1.5 }}>
                            <Avatar sx={{ bgcolor: p.color, width: 40, height: 40, fontSize: '0.95rem', flexShrink: 0 }}>{iniciales(p)}</Avatar>
                            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{nombre}</Typography>
                                    {estadoChip(p)}
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                    {/* Sin fichajes, lo previsto con su horario (con horario y nada previsto, sus no disponibles lo tapan
                                        entero); con fichajes, lo fichado frente a lo previsto */}
                                    {hoy.eventos.length === 0
                                        ? (hoy.esFestivo ? 'Festivo' : !hoy.tieneHorario ? 'Sin horario hoy' : hoy.previstoMs === 0 ? 'No disponible todo el día' : `Previsto ${duracionFichada(hoy.previstoMs)} · ${horario(hoy.tramos)}`)
                                        : hoy.esFestivo
                                            ? `Festivo · fichado ${duracionFichada(hoy.trabajadoMs)}`
                                            : hoy.tieneHorario
                                                ? `Fichado ${duracionFichada(hoy.trabajadoMs)} de ${duracionFichada(hoy.previstoMs)} previstas`
                                                : `Fichado ${duracionFichada(hoy.trabajadoMs)} · sin horario hoy`}
                                </Typography>
                                {/* Fichajes de hoy, cada uno con corregir y anular */}
                                {hoy.eventos.length > 0 && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                                        {hoy.eventos.map(e => (
                                            <Box key={e._id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body2" sx={{ minWidth: 150 }}>{NOMBRE_FICHAJE[e.tipo]} {horaFichaje(e.hora)}</Typography>
                                                {e.origen === 'declarada' && <Tooltip title="Hora declarada"><EditNoteIcon fontSize="small" sx={{ color: 'text.secondary' }} /></Tooltip>}
                                                {e.origen === 'admin' && <Tooltip title={`Corregido por el administrador${e.motivo ? `: ${e.motivo}` : ''}`}><AdminPanelSettingsOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} /></Tooltip>}
                                                <Tooltip title="Corregir la hora">
                                                    <IconButton size="small" onClick={() => setPeticion({ accion: 'corregir', especialistaId: p.especialistaId, nombre, fecha: datos.hoy, evento: e, hora: aHHmm(new Date(e.hora)) })}><EditOutlinedIcon fontSize="small" /></IconButton>
                                                </Tooltip>
                                                <Tooltip title="Anular">
                                                    <IconButton size="small" onClick={() => setPeticion({ accion: 'anular', especialistaId: p.especialistaId, nombre, fecha: datos.hoy, evento: e })}><BlockOutlinedIcon fontSize="small" /></IconButton>
                                                </Tooltip>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                            <Button size="small" startIcon={<AddIcon />} sx={{ ml: { xs: 7, sm: 'auto' }, flexBasis: { xs: 'calc(100% - 56px)', sm: 'auto' }, justifyContent: 'flex-start' }}
                                onClick={() => setPeticion({ accion: 'anadir', especialistaId: p.especialistaId, nombre, fecha: datos.hoy, tipo: hoy.estado === 'enJornada' || hoy.estado === 'enPausa' ? 'salida' : 'entrada' })}>
                                Añadir fichaje
                            </Button>
                        </Paper>
                    );
                })}
            </Box>

            <CorreccionDialog peticion={peticion} onClose={() => setPeticion(null)} onGuardado={guardado} pausas={datos.pausas} />

            <NoDisponibleDialog
                open={noDisponible !== null}
                inicial={noDisponible ?? undefined}
                profesionales={datos.personas.map(p => ({ internalID: p.especialistaId, firstName: p.firstName }))}
                onClose={() => setNoDisponible(null)}
                onGuardado={() => { setAlert({ open: true, severity: 'success', msg: 'No disponible creado' }); cargar(); }}
                onError={(msg) => setAlert({ open: true, severity: 'error', msg })}
            />
        </Box>
    );
};

export default HoyJornada;
